/**
 * DeleteBoardDialogContainer - Container component for delete board
 * confirmation dialog.
 *
 * Manages close handling during deletion and error handling for the delete
 * operation. Renders the shared ConfirmDialog molecule (SE-5524);
 * the former bespoke View's data-driven kanban theme chrome (useKanbanColors
 * + brand delete-red hex waivers) is intentionally standardized onto the
 * molecule's closed-token danger tone. The e2e-pinned
 * `PLAYER_KANBAN.DELETE_BOARD_BUTTON` confirm testID is preserved via
 * override.
 * @module features/player-kanban/components/DeleteBoardDialog
 */

import { AlertTriangle } from "lucide-react-native";
import React, { useCallback } from "react";

import ConfirmDialog from "@/components/molecules/ConfirmDialog";

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/**
 * Props for DeleteBoardDialogContainer component
 */
export interface DeleteBoardDialogProps {
  /** Whether the dialog is visible */
  isOpen: boolean;
  /** Whether deletion is in progress */
  isDeleting?: boolean;
  /** Name of the board to delete */
  boardName: string;
  /** Callback invoked when the dialog is dismissed */
  onClose: () => void;
  /** Async callback invoked when user confirms deletion */
  onConfirmDelete: () => Promise<void>;
}

/**
 * Container component for the delete board confirmation dialog
 * @param root0 - The component props
 * @param root0.isOpen - Whether the dialog is visible
 * @param root0.isDeleting - Whether deletion is in progress
 * @param root0.boardName - Name of the board to delete
 * @param root0.onClose - Callback invoked when the dialog is dismissed
 * @param root0.onConfirmDelete - Async callback invoked when user confirms deletion
 */
const DeleteBoardDialogContainer = ({
  isOpen,
  isDeleting = false,
  boardName,
  onClose,
  onConfirmDelete,
}: DeleteBoardDialogProps) => {
  const handleConfirmDelete = useCallback(async () => {
    try {
      await onConfirmDelete();
    } catch (_error) {
      // Error handling is managed by parent component
    }
  }, [onConfirmDelete]);

  const handleClose = useCallback(() => {
    if (!isDeleting) {
      onClose();
    }
  }, [isDeleting, onClose]);

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirmDelete}
      title="Delete Board"
      body={[
        `Are you sure you want to delete “${boardName}”?`,
        "This action cannot be undone. All players and phases in this board will be permanently removed.",
      ]}
      icon={AlertTriangle}
      tone="danger"
      confirmLabel={isDeleting ? "Deleting..." : "Delete Board"}
      isConfirming={isDeleting}
      testID="delete-board-dialog"
      confirmTestID={TEST_ID_PLAYER_KANBAN.DELETE_BOARD_BUTTON}
    />
  );
};

export default DeleteBoardDialogContainer;
